<template>

  <div id="salesforce-wrapper">
    <el-tabs v-model="activeTab" type="border-card" class="salesforce-tabs">
      <el-tab-pane name="dialer">
        <template slot="label">
          <v-svg class="voxo-blue" sprite="keypad" />
        </template>
        <dialer type="salesforce" />
      </el-tab-pane>
      <el-tab-pane name="contacts">
        <template slot="label">
          <v-svg class="voxo-green" sprite="user" />
        </template>
        <contacts-view />
      </el-tab-pane>
      <el-tab-pane name="queues">
        <template slot="label">
          <v-svg class="voxo-purple" sprite="group-4" />
        </template>
        <queues />
      </el-tab-pane>
      <el-tab-pane name="recent">
        <template slot="label" >
          <v-svg class="voxo-pink" sprite="books" />
        </template>
        <recent-activity />
      </el-tab-pane>
      <el-tab-pane name="settings">
        <template slot="label">
          <v-svg class="voxo-aqua" sprite="gear" />
        </template>
        <settings :tasksSetting="ctiTaskSetting" />
      </el-tab-pane>
    </el-tabs>
  </div>

</template>

<script>

import ContactsView from './tabs/contacts/view';
import Dialer from './tabs/dialer/view';
import Queues from './tabs/queues/view';
import RecentActivity from './tabs/recent-calls/view';
import Settings from './tabs/settings/view';
import moment from 'moment-timezone';
import {globalEventBus} from "@/globalEventBus";
import feathersClient from "@/feathers/feathers-client";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: 'SalesforceIntegration',
  components: {
    Dialer,
    ContactsView,
    Queues,
    RecentActivity,
    Settings
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    softphoneEnabled(){
      return this.$store.state.softphoneEnabled;
    }
  },
  data() {
    return {
      activeTab: 'dialer',
      tasksArray: [],
      ctiTaskSetting: '',
      baseURL: '',
    }
  },
  methods: {

    async handleClickToDial(data) {

     // console.log('click to dial data', data);

      const {number, objectType, recordId, accountId} = data;
      globalEventBus.$emit('salesForceClickToDial', number);
      this.activeTab = 'dialer';

      const taskNumber = this.cleanNumber(number);

      const followFlow = (!this.user.sfTasks || this.ctiTaskSetting === 'Never') && this.ctiTaskSetting !== 'Always';


      //check auto task generation setting, and follow sf search and screen pop if disabled
     // console.log('sf task setting is', this.user.sfTasks)
      if(followFlow){
       // console.log('auto task generation is disabled, following sf search and screen pop')
        this.searchAndScreenPop({cidnum: number, direction: 'initiator', recordId});
        return;
      }

      //set whoId or whatId if we can
      let whoId = null;
      let whatId = null;

      if(objectType === 'Contact' || objectType === 'Lead'){

        console.log('you clicked on a whoId thing!', objectType);

        whoId = recordId;
        if(accountId) whatId = accountId;


        //if we clicked on a contact or lead from an account, opportunity, etc, pass the whatId automatically.
        whatId = await this.checkDialSource();

      } else {

        console.log('you clicked on a whatId thing', objectType);

        //apply whatId and try to find contact or lead with phone
        whatId = recordId;
        const contactLookup = await this.contactLookup(number);
        whoId = contactLookup ? contactLookup : null;
      }

      //dialing from a pending task
      if(objectType === 'Task'){
        this.tasksArray = [...this.tasksArray, {number, taskId: recordId, start: moment()}];
        return;
      }

      //create new voice call object and screen pop
      this.createCallTask({
        number: taskNumber,
        direction: 'initiator',
        whoId,
        whatId
      });

    },

    async checkDialSource() {

      return await new Promise(( resolve, reject ) => {
        sforce.opencti.getAppViewInfo({
          callback: (res) => {
            console.log('here is the app view response', res);
            if(res.success) {
              const { objectType, recordId } = res.returnValue;
              if (objectType !== 'Contact' && objectType !== 'Lead') {
                resolve(recordId);
              } else {
                resolve(null);
              }
            } else {
              resolve(null);
            }

          }
        });
      });

    },

    //looks up a contact or lead based on number
    async contactLookup(number){

      return await new Promise( (resolve, reject) => {

        //try to find the associated record
        sforce.opencti.searchAndScreenPop({
          searchParams: number,
          callType: sforce.opencti.CALL_TYPE.INBOUND,
          deferred: true,
          callback: (res) => {

           // console.log('search results from incoming call accept', res);

            const { returnValue } = res;
            let whoId = null;
            let foundRecord = null;
            for(let key in returnValue){
              if(key !== 'SCREEN_POP_DATA'){
                const record = returnValue[key];
                if(record.RecordType === 'Contact' || record.RecordType === 'Lead'){
                  foundRecord = record;
                  break;
                }
              }
            }

            if(foundRecord){
              if(foundRecord.RecordType === 'Contact' || foundRecord.RecordType === 'Lead'){
                whoId = foundRecord.Id
              }
            }

            resolve(whoId);
          }
        });

      });

    },

    cleanNumber(number) {
      //sanitize any characters we do not consider
      number = number.replace(/['":/{}()[|\-.<>?_=+ \]]/g, '');

      //checking for a delimiter of type comma
      if(number.includes(',')){
        return number.split(',')[0];
      }

      //checking for delimiter of type semicolon
      if(number.includes(';')){
        return number.split(';')[0]
      }

      return number;
    },

    //create a task related to the inbound or outbound calls
    createCallTask(data) {

      const {number, direction, whoId, whatId} = data;

      const callSubject = direction === 'initiator' ? 'Outbound Call' : 'Inbound Call'

      const taskObject = {
        entityApiName: 'Task',
        ActivityDate: moment().format('YYYY-MM-DD'),
        TaskSubtype: 'Call',
        Call_Type__c: callSubject,
        Call_Duration__c: 0,
        Subject: callSubject,
        Phone_Number__c: number
      };

      if(whoId){
        taskObject.WhoId = whoId;
      }

      if(whatId) {
        taskObject.WhatId = whatId;
      }

      sforce.opencti.saveLog({
        value: taskObject,
        callback: (res) => {
        //  console.log('save log response', res);
          const { returnValue } = res;
          if(returnValue){
            this.tasksArray = [...this.tasksArray, {number, taskId: returnValue.recordId, start: moment()}];
           // console.log('new tasks array', this.tasksArray);
            sforce.opencti.screenPop({
              type: sforce.opencti.SCREENPOP_TYPE.SOBJECT,
              params: {
                recordId: returnValue.recordId
              }
            });
          }
        }
      })
    },

    //update existing task if there is one and remove from array
    updateCallTask(data){


    //  console.log('here is the task data', data);

      const { taskId, duration } = data;

      const updateBody = {
        Id: taskId,
        CallDurationInSeconds: duration,
        Call_Duration__c: duration
      };

      sforce.opencti.saveLog({
        value: updateBody,
        callback: (res) => {
          //console.log('update task callback', res);
          this.tasksArray = this.tasksArray.filter(item => item.taskId !== taskId);
         // console.log('new tasks array', this.tasksArray);
        }
      });

    },

    //handle the event from the user requesting a salesforce entity search
    handleSearchAndPop(data) {

      const {type, cidnum, direction} = data;

      const callType = direction === 'initiator'
        ? sforce.opencti.CALL_TYPE.OUTBOUND
        : sforce.opencti.CALL_TYPE.INBOUND

      sforce.opencti.searchAndScreenPop({
        searchParams: cidnum,
        callType,
        deferred: true,
        callback: (res) => {
          this.doScreenPop(type, res);
        }
      });

    },

    //opinionated screen pop controlled by user button click
    doScreenPop(type, data){

      const { returnValue } = data;
      let foundRecord = null;
      for(let key in returnValue){
        if(key !== 'SCREEN_POP_DATA'){
          const record = returnValue[key];
          if(record.RecordType === type){
            foundRecord = record;
            break;
          }
        }
      }

      if(foundRecord){
        sforce.opencti.screenPop({
          type: sforce.opencti.SCREENPOP_TYPE.SOBJECT,
          params: { recordId: foundRecord.Id }
        });
        return;
      }

      this.$confirm( `No ${type} found. Would you like to create one?`, {
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then( () => {
        this.popFormModal(type);
      }).catch( () => {
        return true;
      });

    },

    //follows the softphone layout specification
    searchAndScreenPop(data) {

     // console.log('search and screen pop called', data);

      const {cidnum, direction, recordId} = data;

      const callType = direction === 'initiator'
        ? sforce.opencti.CALL_TYPE.OUTBOUND
        : sforce.opencti.CALL_TYPE.INBOUND

      let params = {
        searchParams: cidnum,
        callType,
        deferred: false
      }

      if(recordId){
        params.params = { FLOW: { flowArgs: [{ name: 'recordId', type: 'String', value: recordId}]}};
        sforce.opencti.searchAndScreenPop(params);
        return;
      }

      //we can try our best to look up the record id here
      //try to find the associated record if one wasn't passed to us.
      sforce.opencti.searchAndScreenPop({
        searchParams: cidnum,
        callType,
        deferred: true,
        callback: (res) => {

          const { returnValue } = res;
          let foundRecord = null;
          for(let key in returnValue){
            if(key !== 'SCREEN_POP_DATA'){
              const record = returnValue[key];
              if(!['Contact', 'Lead'].includes(record.RecordType)){
                foundRecord = record;
                break;
              }
            }
          }

          if(foundRecord){
            params.params = { FLOW: { flowArgs: [{ name: 'recordId', type: 'String', value: foundRecord.Id}]}};
          }
          sforce.opencti.searchAndScreenPop(params);
        }
      });

    },

    //screen pop for creating a new entity
    popFormModal(type) {
      sforce.opencti.screenPop({
        type: sforce.opencti.SCREENPOP_TYPE.NEW_RECORD_MODAL,
        params: { entityName: type}
      });
    },

    numberFormatter(number) {
      return number
        ? phoneNumberFormatter(number)
        : number;
    },

    async setupCTI(){

      //force light mode
      document.body.setAttribute('class', 'theme-default');

      //get the tenant setting for cti task generation
      const tenantQuery = await feathersClient.service('tenants').find({
        query: {
          id: this.user.tenantId
        }
      });

      if(tenantQuery.length) {
        this.ctiTaskSetting = tenantQuery[0].salesforceCTITasks;
        this.baseURL = tenantQuery[0].salesforceDomain;
      }

      if(!this.baseURL){
        this.$notify({
          title: 'Error',
          message: 'Your account is not configured for Salesforce CTI. Please contact your administrator.',
          type: 'error'
        });
        return;
      }

      //we will need to grab the url from somewhere. likely db setting or something?
      let sfScript = document.createElement('script');
      sfScript.setAttribute('src', `${this.baseURL}/support/api/54.0/lightning/opencti_min.js`);
      document.head.appendChild(sfScript);

      console.log(this.baseURL);

      console.log(sfScript);

      console.log(document);

      console.log(document.head);


      //force enable click to dial in salesforce
      setTimeout( () => {
        sforce.opencti.enableClickToDial({
          callback: () => {
            //console.log('SF Click to Dial Is Ready');
            return true;
          }
        });

        //click to dial event listener
        sforce.opencti.onClickToDial({
          listener: this.handleClickToDial
        })
      }, 1000);

    }

  },
  created() {

    //Softphone Event Listeners
    globalEventBus.$on('globalCallHangup', data => {

      const { cidnum } = data;

      //console.log('got a hangup here are the current tasks', this.tasksArray);


      //look up the cidnum
      const theTask = this.tasksArray.find(item => item.number === cidnum);

      //console.log('found the task', theTask);

      //if there is a task initiated, update it
      if(theTask){
        const duration = moment().diff(theTask.start, 'seconds');
        this.updateCallTask({
          taskId: theTask.taskId,
          duration
        });
      }

    });

    //incoming call was answered via softphone
    globalEventBus.$on('softphoneCallAccepted', data => {
      const {cidNum} = data;

      //check sf task generation setting before creating a call task
      const followFlow = (!this.user.sfTasks || this.ctiTaskSetting === 'Never') && this.ctiTaskSetting !== 'Always';
      //console.log('user sf task setting', this.user.sfTasks);
      if(followFlow){
        this.searchAndScreenPop({cidnum: cidNum, direction: 'recipient'});
        return;
      }

      //try to find the associated record
      sforce.opencti.searchAndScreenPop({
        searchParams: cidNum,
        callType: sforce.opencti.CALL_TYPE.INBOUND,
        deferred: true,
        callback: (res) => {

         // console.log('search results from incoming call accept', res);

          const { returnValue } = res;
          let foundRecord = null;
          for(let key in returnValue){
            if(key !== 'SCREEN_POP_DATA'){
              const record = returnValue[key];
              if(record.RecordType === 'Account' || record.RecordType === 'Contact' || record.RecordType === 'Lead'){
                foundRecord = record;
                break;
              }
            }
          }

          if(foundRecord){
            let whatId = null;
            let whoId = null;
            if(foundRecord.RecordType === 'Account'){
              whatId = foundRecord.Id
            }

            if(foundRecord.RecordType === 'Contact' || foundRecord.RecordType === 'Lead'){
              whoId = foundRecord.Id
            }

            this.createCallTask({
              number: cidNum,
              direction: 'recipient',
              whoId,
              whatId
            });
          }
        }
      });
    });

    //direct dial from the widget in the crm
    globalEventBus.$on('crmWidgetDial', number => {

      //console.log('received widget dial', number);

      const taskNumber = this.cleanNumber(number);
      this.activeTab = 'dialer';

      //check sf task generation setting before creating a call task
      const followFlow = (!this.user.sfTasks || this.ctiTaskSetting === 'Never') && this.ctiTaskSetting !== 'Always';

      //console.log('user sf task setting', this.user.sfTasks);
      if(followFlow){
        this.searchAndScreenPop({cidnum: taskNumber, direction: 'initiator'});
        return;
      }

      //try to find the associated record
      sforce.opencti.searchAndScreenPop({
        searchParams: taskNumber,
        callType: sforce.opencti.CALL_TYPE.OUTBOUND,
        deferred: true,
        callback: (res) => {

         // console.log('search results from widget dialer dial', res);

          const { returnValue } = res;
          let foundWhat = null;
          let foundWho = null;
          for(let key in returnValue){
            if(key !== 'SCREEN_POP_DATA'){
              const record = returnValue[key];
              if(record.RecordType === 'Contact' || record.RecordType === 'Lead'){
                foundWho = record;
              } else {
                foundWhat = record;
              }

              if(foundWho && foundWhat){
                break;
              }
            }
          }

          if(foundWho || foundWhat){
            let whatId = null;
            let whoId = null;
            if(foundWhat){
              whatId = foundWhat.Id
            }

            if(foundWho){
              whoId = foundWho.Id
            }

            this.createCallTask({
              number: taskNumber,
              direction: 'initiator',
              whoId,
              whatId
            });
          }
        }
      });

    })


    //user requested an entity search from salesforce
    globalEventBus.$on('entitySearchAndScreenPop', data => {
      this.handleSearchAndPop(data);
    });

    //follow flow defined by salesforce softphone layout
    globalEventBus.$on('searchAndScreenPop', data => {
      this.searchAndScreenPop(data);
    })

    //handle opening a new create form modal
    globalEventBus.$on('openNewEntityForm', type => {
      this.popFormModal(type);
    });

  },
  mounted() {

    this.setupCTI();

  }
}

</script>

<style lang="scss">

#salesforce-wrapper {
  max-width: 450px;
}

.salesforce-tabs{
  display: flex;
  flex-direction: column-reverse;
  .el-tab-pane{
    height: 433px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .el-tabs__content{
    padding: 5px;
  }

  .el-tabs__item {
    line-height: 48px;
  }

  .current-call-item{
    margin-left: 0;
  }
}

@media(max-width: 768px) {
  #salesforce-wrapper.view {
    padding: 0;
  }
}

</style>
